import { type ConfigFilter, type ConfigObject, DebugLevel } from '../../../_configs/types';
import getCar from '../../../_tools/axios/cars/getCar';
import handleCarNavigateData from '../../../_tools/axios/cars/helpers/handleCarNavigateData';
import type { Facets } from '../../../_types/Car';
import { generateLink } from '../../../utils/generateLink';
import stringToSlug from '../../../utils/string-to-slug';
import checkUndefinedNullOrEmpty from '../../../utils/tools/checkUndefinedNullOrEmpty';
import enhancedNavigate from '../../../utils/tools/enhancedNavigate';
import type { FilterMap } from '../filter/maps/filterMap';
import getFilterFromParamsAndQuery from '../filter/utils/getFilterFromParamsAndQuery';
import getUrlFromFilter from '../filter/utils/getUrlFromFilter';
import { fahrzeugsucheModuleName } from '../types';

export default async function checkOldUrls(loc: Location, allFacets: Facets, filterElements: FilterMap, settings: ConfigObject) {
    const checkMagicLink = loc?.pathname?.match(/\/magicId\/([0-9]+)/);
    if (!checkUndefinedNullOrEmpty(checkMagicLink?.[1])) {
        try {
            const rawCar = (await getCar(parseFloat(checkMagicLink?.[1]), settings?.general?.idFirma, settings?.fahrzeugdetail?.similarCarsFilter, undefined, undefined, settings?.general?.debug))?.data;
            const carLink = generateLink({ car: await handleCarNavigateData(rawCar), module: fahrzeugsucheModuleName, pathOnly: false, settings, type: 'car' });
            if (!checkUndefinedNullOrEmpty(rawCar)) {
                if (settings?.general?.debug === DebugLevel.ROUTER || settings?.general?.debug >= DebugLevel.INFO) {
                    console.log(`${fahrzeugsucheModuleName} - checkOldUrls - Redirect magicId - Redirecting to new url`, carLink);
                }
                enhancedNavigate(carLink, undefined, true);
            }
        } catch {
            //
        }
    }

    const checkSourceLink = loc?.search?.match(/(\?|&)source=([0-9]+)/);
    if (!checkUndefinedNullOrEmpty(checkSourceLink?.[2])) {
        try {
            const rawCar = (await getCar(parseFloat(checkSourceLink?.[2]), settings?.general?.idFirma, settings?.fahrzeugdetail?.similarCarsFilter, undefined, undefined, settings?.general?.debug))?.data;
            const carLink = generateLink({ car: await handleCarNavigateData(rawCar), module: fahrzeugsucheModuleName, pathOnly: false, settings, type: 'car' });
            if (!checkUndefinedNullOrEmpty(rawCar)) {
                if (settings?.general?.debug === DebugLevel.ROUTER || settings?.general?.debug >= DebugLevel.INFO) {
                    console.log(`${fahrzeugsucheModuleName} - checkOldUrls - Redirect source - Redirecting to new url`, carLink);
                }
                enhancedNavigate(carLink, undefined, true);
            }
        } catch {
            //
        }
    }

    const checkOldEinzelansicht = loc?.search?.match(/_carId([0-9]+)/);
    if (!checkUndefinedNullOrEmpty(checkOldEinzelansicht?.[1])) {
        try {
            const rawCar = (await getCar(parseFloat(checkOldEinzelansicht?.[1]), settings?.general?.idFirma, settings?.fahrzeugdetail?.similarCarsFilter, undefined, undefined, settings?.general?.debug))?.data;
            const carLink = generateLink({ car: await handleCarNavigateData(rawCar), module: fahrzeugsucheModuleName, pathOnly: false, settings, type: 'car' });
            if (!checkUndefinedNullOrEmpty(rawCar)) {
                if (settings?.general?.debug === DebugLevel.ROUTER || settings?.general?.debug >= DebugLevel.INFO) {
                    console.log(`${fahrzeugsucheModuleName} - checkOldUrls - Redirect _carId - Redirecting to new url`, carLink);
                }
                enhancedNavigate(carLink, undefined, true);
            }
        } catch {
            //
        }
    }

    const pathnameOldNew = loc?.pathname?.replace(settings?.general?.searchPath, '');
    const checkOldNewEinzelansicht = loc?.pathname?.startsWith('/c/') ? undefined : pathnameOldNew?.match(/\/[A-z0-9-]+/g);
    if (!checkUndefinedNullOrEmpty(checkOldNewEinzelansicht) && checkOldNewEinzelansicht?.length === 6) {
        try {
            const carId = checkOldNewEinzelansicht[5]?.replace('/', '');
            const rawCar = (await getCar(parseFloat(carId), settings?.general?.idFirma, settings?.fahrzeugdetail?.similarCarsFilter, undefined, undefined, settings?.general?.debug))?.data;
            const carLink = generateLink({ car: await handleCarNavigateData(rawCar), module: fahrzeugsucheModuleName, pathOnly: false, settings, type: 'car' });
            if (!checkUndefinedNullOrEmpty(rawCar)) {
                if (settings?.general?.debug === DebugLevel.ROUTER || settings?.general?.debug >= DebugLevel.INFO) {
                    console.log(`${fahrzeugsucheModuleName} - checkOldUrls - Redirect newVehicleEinzelansicht - Redirecting to new url`, carLink);
                }
                enhancedNavigate(carLink, undefined, true);
            }
        } catch {
            //
        }
    }

    const query: URLSearchParams = new URLSearchParams(loc?.search);
    const convertedParams: Partial<{ [key in ConfigFilter]: string }> = {};
    const herstellerQueryValue = query?.get('hersteller0');
    if (!checkUndefinedNullOrEmpty(herstellerQueryValue)) {
        convertedParams.hersteller = stringToSlug(herstellerQueryValue);
    }
    const modellQueryValue = query?.get('modell0');
    if (!checkUndefinedNullOrEmpty(modellQueryValue)) {
        convertedParams.modell = stringToSlug(modellQueryValue);
    }
    const aktTypListQueryValue = query?.get('aktTypList');
    if (!checkUndefinedNullOrEmpty(aktTypListQueryValue)) {
        convertedParams.typ = stringToSlug(aktTypListQueryValue);
    }
    const standortQueryValue = query?.get('Standort');
    if (!checkUndefinedNullOrEmpty(standortQueryValue)) {
        convertedParams.standort = standortQueryValue
            ?.split(',')
            ?.map((val) => stringToSlug(val))
            ?.join(',');
    }
    const firmenQueryValue = query?.get('idFirma');
    if (!checkUndefinedNullOrEmpty(firmenQueryValue)) {
        convertedParams.standort = firmenQueryValue
            ?.split(',')
            ?.map((val) => stringToSlug(val))
            ?.join(',');
    }
    if (!checkUndefinedNullOrEmpty(convertedParams)) {
        const between: Partial<{ [key in ConfigFilter]: string }> = {};
        query?.forEach((val, key) => {
            if (!['hersteller0', 'modell0', 'Standort', 'idFirma'].includes(key)) {
                const queryKey = Object.keys(filterElements)?.find((v) => filterElements?.[v as ConfigFilter]?.filterKey === key);
                if (queryKey?.endsWith('Von')) {
                    const actualConvertedQueryKey = queryKey?.replace('Von', '');
                    if (checkUndefinedNullOrEmpty(between[actualConvertedQueryKey])) {
                        between[actualConvertedQueryKey] = [];
                    }
                    between[actualConvertedQueryKey][0] = val;
                } else if (queryKey?.endsWith('Bis')) {
                    const actualConvertedQueryKey = queryKey?.replace('Bis', '');
                    if (checkUndefinedNullOrEmpty(between[actualConvertedQueryKey])) {
                        between[actualConvertedQueryKey] = [];
                    }
                    between[actualConvertedQueryKey][1] = val;
                } else {
                    between[queryKey] = val?.includes(',')
                        ? val
                              ?.split(',')
                              ?.map((v) => stringToSlug(v))
                              ?.join(',')
                        : stringToSlug(val);
                }
            }
        });
        const convertedQuery: URLSearchParams = new URLSearchParams(between);
        const convertedSearch = getFilterFromParamsAndQuery(allFacets, settings?.general?.useFirmenFilter, convertedParams, convertedQuery, settings?.general?.useOldRouter);
        const newUrl = getUrlFromFilter(allFacets, settings, convertedSearch, fahrzeugsucheModuleName, false);
        if (!newUrl.noPushToPage === false) {
            enhancedNavigate(newUrl?.url, undefined, true);
        }
    }
}
